/**
 * VehicleFinesDialog - Multas do veículo (Fase 4 do módulo Veículos)
 *
 * Modal premium que lista as multas já sincronizadas do veículo (leitura via
 * RPC `list_vehicle_fines`) e permite disparar uma nova sincronização sob
 * demanda através da Edge Function `plate-proxy` (multi-provider).
 *
 * Regras:
 *  - A sincronização é sempre manual (controle de custo por consulta).
 *  - Requer placa válida cadastrada no veículo.
 *  - O botão de sincronizar só aparece para quem tem permissão de consulta.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (aba/modal de Multas)
 */

import { AlertTriangle, Loader2, RefreshCw, ReceiptText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InlineNotice } from '@/components/ui/inline-notice';
import { useVehicleFines, isValidPlate } from '@/hooks/usePlateLookup';

/** Props do modal de multas */
interface VehicleFinesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Veículo alvo (id + placa) */
  vehicleId: string | null;
  plate: string | null;
  /** Descrição curta (marca/modelo) exibida no cabeçalho */
  subtitle?: string | null;
  /** Permite disparar nova consulta ao provedor */
  canSync?: boolean;
  /** Callback após sincronizar (para atualizar a listagem/KPIs) */
  onSynced?: () => void;
}

/** Formata valores monetários em BRL */
const formatCurrency = (value: number | null | undefined) =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/** Formata datas ISO/BR para dd/MM/yyyy (sem deslocamento de fuso) */
const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const onlyDate = value.slice(0, 10);
  const [year, month, day] = onlyDate.split('-');
  if (year && month && day) return `${day}/${month}/${year}`;
  return value;
};

/** Badge visual por status da multa */
function FineStatusBadge({ status }: { status: string }) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'paid' || normalized === 'paga') {
    return <Badge className="bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15">Paga</Badge>;
  }
  if (normalized === 'cancelled' || normalized === 'cancelada') {
    return <Badge variant="outline">Cancelada</Badge>;
  }
  return <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/15">Em aberto</Badge>;
}

/** Modal de multas do veículo */
export function VehicleFinesDialog({
  open,
  onOpenChange,
  vehicleId,
  plate,
  subtitle,
  canSync = true,
  onSynced,
}: VehicleFinesDialogProps) {
  const { fines, syncedAt, totalOpen, isLoading, sync } = useVehicleFines(vehicleId, open);

  const plateValid = !!plate && isValidPlate(plate);

  /** Dispara a sincronização de multas com o provedor */
  const handleSync = async () => {
    if (!plate) return;
    await sync.mutateAsync({ plate, force: true });
    onSynced?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 p-0">
        {/* Cabeçalho premium */}
        <DialogHeader className="border-b px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10">
                <ReceiptText className="h-4.5 w-4.5 text-amber-600" />
              </div>
              <div>
                <DialogTitle className="text-base">Multas do veículo</DialogTitle>
                <DialogDescription className="text-xs">
                  {[plate || 'Sem placa', subtitle].filter(Boolean).join(' · ')}
                  {syncedAt && ` · Última consulta em ${formatDate(syncedAt)}`}
                </DialogDescription>
              </div>
            </div>

            {canSync && (
              <Button
                size="sm"
                variant="outline"
                className="mr-6 gap-1.5"
                disabled={!plateValid || sync.isPending}
                onClick={handleSync}
              >
                {sync.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Atualizar multas
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-3 px-5 py-4">
          {!plateValid && (
            <InlineNotice variant="warning">
              Cadastre uma placa válida no veículo para consultar multas.
            </InlineNotice>
          )}

          {/* Resumo */}
          <div className="flex items-center justify-between rounded-lg border px-3 py-2">
            <span className="text-xs text-muted-foreground">Total em aberto</span>
            <span className="text-sm font-semibold text-amber-600">{formatCurrency(totalOpen)}</span>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          ) : fines.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-10 text-center">
              <AlertTriangle className="h-7 w-7 text-muted-foreground/60" />
              <p className="text-sm font-medium">Nenhuma multa encontrada</p>
              <p className="text-xs text-muted-foreground">
                Use "Atualizar multas" para consultar o provedor.
              </p>
            </div>
          ) : (
            <ScrollArea className="max-h-[52vh]">
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Infração</th>
                      <th className="px-3 py-2 text-left font-medium">Órgão</th>
                      <th className="px-3 py-2 text-left font-medium">Data</th>
                      <th className="px-3 py-2 text-left font-medium">Vencimento</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fines.map((fine, index) => (
                      <tr key={fine.id ?? `${fine.auto_infracao}-${index}`} className="border-t">
                        <td className="max-w-[280px] px-3 py-2">
                          <p className="truncate font-medium">{fine.descricao || 'Infração'}</p>
                          {fine.auto_infracao && (
                            <p className="truncate text-xs text-muted-foreground">
                              AIT {fine.auto_infracao}
                            </p>
                          )}
                        </td>
                        <td className="max-w-[160px] truncate px-3 py-2">{fine.orgao || '—'}</td>
                        <td className="px-3 py-2">{formatDate(fine.data_infracao)}</td>
                        <td className="px-3 py-2">{formatDate(fine.vencimento)}</td>
                        <td className="px-3 py-2">
                          <FineStatusBadge status={fine.status} />
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {formatCurrency(fine.valor)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
